/**
 * What the CI fleet screen shows, worked out without React. org-admin#96.
 *
 * Pure and exported for tests, the same way `route.ts` and `format.ts` are: the
 * part of a screen that can be wrong in a way worth catching is the arithmetic
 * and the wording, not the markup around it.
 */
import type { BusyHour, Liveness } from './fleet-api.js';
import { count } from './format.js';

export type Tone = 'good' | 'warn' | 'danger' | 'muted';

/**
 * A runner's state in words. GitHub's `status` is never the headline: runners
 * on the Actions v2 broker flow report `offline` while working
 * (actions/runner#3892), and the 12 Sep comment on #96 is why this reads the
 * derived liveness instead.
 */
export function livenessView(liveness: Liveness): { tone: Tone; label: string } {
  switch (liveness) {
    case 'online':
      return { tone: 'good', label: 'Online' };
    case 'disputed':
      // The everyday state while #3892 is open. Good, not warn: the runner IS
      // working, and a panel that paints the normal state amber gets ignored.
      return { tone: 'good', label: 'Connected, GitHub reports offline' };
    case 'unknown':
      // GitHub says offline and nothing has run for 15 minutes. Idle and down
      // look the same from here, so the words must not pick one.
      return { tone: 'muted', label: 'No recent work, GitHub reports offline' };
  }
}

export interface TickMeta {
  last_tick_at: string | null;
  last_tick_attempt_at: string | null;
  last_tick_error: string | null;
}

/** A tick is every minute, so three minutes without one is not a blip. */
export const TICK_STALE_MS = 3 * 60 * 1000;

/**
 * The collector's state, including the one that must not read as fine.
 *
 * From the fleet-api#17 note on #96: attempt current, success stale, error
 * EMPTY means the collector is failing and could not record why (D1 refusing
 * writes, or the runtime killing the tick). It gets its own words.
 */
export function tickView(meta: TickMeta, now = Date.now()): { tone: Tone; label: string } {
  const fresh = (iso: string | null) => iso !== null && now - Date.parse(iso) <= TICK_STALE_MS;
  if (!meta.last_tick_attempt_at && !meta.last_tick_at) return { tone: 'muted', label: 'Collector has never run' };
  if (fresh(meta.last_tick_at)) return { tone: 'good', label: 'Collecting' };
  if (!fresh(meta.last_tick_attempt_at)) return { tone: 'danger', label: 'Collector is not running' };
  if (meta.last_tick_error) return { tone: 'danger', label: `Collector failing: ${meta.last_tick_error}` };
  return { tone: 'danger', label: 'Collector failing, reason not recordable. Check the Worker logs' };
}

/** No key press, click, scroll or pointer movement for this long, and polling stops. */
export const IDLE_PAUSE_MS = 30 * 60 * 1000;

/**
 * Whether a poll tick should fetch.
 *
 * Not only a matter of load. Every fetch here spends a fleet-api token, and an
 * expired one is renewed by redirecting the whole page (lib/fleet-token.ts). A
 * dashboard left open in a background tab or on an unattended screen would
 * otherwise do that four times an hour for nobody (#96, Option A).
 */
export function shouldPoll(visible: boolean, lastActivityAt: number, now = Date.now()): boolean {
  return visible && now - lastActivityAt < IDLE_PAUSE_MS;
}

/**
 * Busy runner-seconds by weekday and hour, in the VIEWER's time zone.
 *
 * fleet-api sends per-clock-hour totals rather than a grid precisely so this
 * can happen here: a grid bucketed in UTC could not be moved into local time.
 * Rows are Monday first. `local` is injectable so a test is not at the mercy of
 * the machine's zone.
 */
export function heatmap(
  hours: BusyHour[],
  local: (d: Date) => { weekday: number; hour: number } = (d) => ({ weekday: d.getDay(), hour: d.getHours() }),
): { cells: number[][]; max: number } {
  const cells = Array.from({ length: 7 }, () => Array<number>(24).fill(0));
  let max = 0;
  for (const h of hours) {
    const { weekday, hour } = local(new Date(h.hour));
    const row = (weekday + 6) % 7; // Sunday 0 → row 6, Monday 1 → row 0
    const total = cells[row]![hour]! + h.busy_runner_s;
    cells[row]![hour] = total;
    max = Math.max(max, total);
  }
  return { cells, max };
}

/** Every day from `since` to `until` inclusive, as `YYYY-MM-DD`. */
export function dayRange(since: string, until: string): string[] {
  const days: string[] = [];
  const end = Date.parse(`${until}T00:00:00Z`);
  for (let t = Date.parse(`${since}T00:00:00Z`); t <= end; t += 86_400_000) {
    days.push(new Date(t).toISOString().slice(0, 10));
  }
  return days;
}

/**
 * SVG polyline points for one series: x across `days`, y scaled to `max`.
 *
 * A day with no value BREAKS the line (a new segment) rather than dropping to
 * zero or joining its neighbours: "no passing run that day" is not "0 seconds",
 * and a line drawn straight across a gap claims data that is not there.
 */
export function seriesSegments(
  days: string[],
  valueOn: (day: string) => number | null,
  box: { width: number; height: number; max: number },
): string[] {
  const segments: string[] = [];
  let current: string[] = [];
  const step = days.length > 1 ? box.width / (days.length - 1) : 0;
  days.forEach((day, i) => {
    const v = valueOn(day);
    if (v === null || box.max <= 0) {
      if (current.length) segments.push(current.join(' '));
      current = [];
      return;
    }
    const x = Math.round(i * step * 10) / 10;
    const y = Math.round((box.height - (Math.min(v, box.max) / box.max) * box.height) * 10) / 10;
    current.push(`${x},${y}`);
  });
  if (current.length) segments.push(current.join(' '));
  return segments;
}

/** Dollars to the cent, with a plain minus. */
export function usd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const abs = Math.abs(n).toFixed(2);
  return n < 0 ? `-$${abs}` : `$${abs}`;
}

export function minutes(n: number | null | undefined): string {
  return n === null || n === undefined ? '—' : `${count(n)} min`;
}

/** A wait or a run time: `45s`, `4m 12s`, `1h 03m`. */
export function seconds(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '—';
  const whole = Math.max(0, Math.round(s));
  if (whole < 60) return `${whole}s`;
  const m = Math.floor(whole / 60);
  if (m < 60) return `${m}m ${String(whole % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/**
 * A share as a percentage, precise enough that small is not rounded to nothing.
 *
 * All six runners busy at once was 0.002% of the time in the first week. Shown
 * as "0%", the capacity panel would claim it never happened; shown to one
 * decimal, the same.
 */
export function share(fraction: number | null | undefined): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return '—';
  if (fraction === 0) return '0%';
  const pct = fraction * 100;
  if (pct < 0.01) return `${pct.toFixed(3)}%`;
  if (pct < 1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
}
